import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import Backdrop from './Backdrop'

interface EditMessageModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  initialText: string
  onConfirm: (newContent: string) => void | Promise<void>
}

export default function EditMessageModal({
  open,
  onOpenChange,
  initialText,
  onConfirm,
}: EditMessageModalProps) {
  const [text, setText] = useState(initialText)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    setText(initialText)
  }, [initialText, open])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const t = text.trim()
    if (!t || saving) return
    setSaving(true)
    try {
      await Promise.resolve(onConfirm(t))
      onOpenChange(false)
    } catch {
      // keep open; parent may have surfaced error state
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  if (typeof document === 'undefined') return null

  return createPortal(
    <Backdrop
      onClose={() => !saving && onOpenChange(false)}
      style={{
        zIndex: 10050,
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(15, 30, 56, 0.55)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <div
        style={{
          background: 'var(--white)',
          borderRadius: 20,
          width: 420,
          maxWidth: '94vw',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'var(--sh-lg)',
          overflow: 'hidden',
          animation: 'mIn .4s cubic-bezier(.16,1,.3,1) both',
          margin: 'auto',
          position: 'relative',
          flexShrink: 0,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          style={{
            padding: '20px 26px 16px',
            borderBottom: '1px solid var(--bd2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <div>
            <h3
              style={{
                fontFamily: "'Playfair Display',serif",
                fontSize: 17,
                fontWeight: 500,
                color: 'var(--navy)',
              }}
            >
              Edit message
            </h3>
            <p style={{ fontSize: 11.5, color: 'var(--muted)', marginTop: 2 }}>
              Update your message and save to get a new reply
            </p>
          </div>
          <button
            type="button"
            disabled={saving}
            onClick={() => onOpenChange(false)}
            style={{
              width: 28,
              height: 28,
              borderRadius: 7,
              border: '1px solid var(--bd)',
              background: 'transparent',
              cursor: saving ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 15,
              color: 'var(--muted)',
              opacity: saving ? 0.6 : 1,
            }}
          >
            ✕
          </button>
        </div>
        <form
          onSubmit={handleSubmit}
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <div
            style={{
              padding: '20px 26px',
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
            }}
          >
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Your message"
              autoFocus
              disabled={saving}
              style={{
                width: '100%',
                minHeight: 140,
                maxHeight: 'min(52vh, 480px)',
                padding: '10px 12px',
                fontSize: 14,
                lineHeight: 1.6,
                color: 'var(--text)',
                background: 'var(--bg2)',
                border: '1px solid var(--bd)',
                borderRadius: 9,
                outline: 'none',
                boxSizing: 'border-box',
                resize: 'vertical',
                fontFamily: 'inherit',
              }}
            />
          </div>
          <div
            style={{
              padding: '14px 26px 20px',
              borderTop: '1px solid var(--bd2)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              flexShrink: 0,
              background: 'var(--white)',
            }}
          >
            <button
              type="button"
              disabled={saving}
              onClick={() => onOpenChange(false)}
              style={{
                padding: '7px 16px',
                borderRadius: 9,
                border: '1px solid var(--bd)',
                background: 'transparent',
                fontSize: 12.5,
                fontWeight: 500,
                color: 'var(--muted)',
                cursor: saving ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!text.trim() || saving}
              style={{
                padding: '7px 16px',
                borderRadius: 9,
                border: 'none',
                background: 'var(--navy)',
                fontSize: 12.5,
                fontWeight: 500,
                color: 'var(--white)',
                cursor: text.trim() && !saving ? 'pointer' : 'not-allowed',
                opacity: text.trim() && !saving ? 1 : 0.6,
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </Backdrop>,
    document.body
  )
}
